import React, { useState } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, StrikethroughIcon, LinkIcon, CodeIcon, QuoteIcon, ListIcon, ListOrderedIcon, AlignLeftIcon, PencilIcon, TrashIcon, PlusIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
type Section = 'departements' | 'categories' | 'statuts' | 'messages';
type Localized = {
  fr: string;
  en: string;
};
interface Departement {
  id: string;
  nom: Localized;
  responsable: string;
}
interface Categorie {
  id: string;
  nom: Localized;
  departementId: string;
  sla: number;
}
interface Statut {
  id: string;
  nom: Localized;
  couleur: string;
}
interface MessageTpl {
  id: string;
  titre: Localized;
  contenu: Localized;
}
const MOCK_DEPARTEMENTS: Departement[] = [{
  id: '1',
  nom: {
    fr: 'Service des travaux publics',
    en: 'Public Works Department'
  },
  responsable: 'Jean Dupont'
}, {
  id: '2',
  nom: {
    fr: 'Urbanisme',
    en: 'Urban Planning'
  },
  responsable: 'Marie Tremblay'
}];
const MOCK_CATEGORIES: Categorie[] = [{
  id: '1',
  nom: {
    fr: 'Voirie et infrastructures routières',
    en: 'Roads and road infrastructure'
  },
  departementId: '1',
  sla: 14
}, {
  id: '2',
  nom: {
    fr: 'Parcs et espaces récréatifs',
    en: 'Parks and recreational spaces'
  },
  departementId: '1',
  sla: 7
}, {
  id: '3',
  nom: {
    fr: 'Bâtiment',
    en: 'Building'
  },
  departementId: '2',
  sla: 30
}];
const MOCK_STATUTS: Statut[] = [{
  id: '1',
  nom: {
    fr: 'Nouvelle demande',
    en: 'New request'
  },
  couleur: 'bg-slate-200'
}, {
  id: '2',
  nom: {
    fr: 'En cours',
    en: 'In progress'
  },
  couleur: 'bg-[#4F46E5]'
}, {
  id: '3',
  nom: {
    fr: 'Complété',
    en: 'Completed'
  },
  couleur: 'bg-[#4F46E5]'
}];
const MOCK_MESSAGES: MessageTpl[] = [{
  id: '1',
  titre: {
    fr: 'Confirmation de réception',
    en: 'Receipt confirmation'
  },
  contenu: {
    fr: 'Votre requête a bien été reçue et sera traitée dans les plus brefs délais.',
    en: 'Your request has been received and will be processed as soon as possible.'
  }
}, {
  id: '2',
  titre: {
    fr: 'Requête complétée',
    en: 'Request completed'
  },
  contenu: {
    fr: 'Nous vous informons que les travaux liés à votre requête sont maintenant terminés.',
    en: 'We are pleased to inform you that the work related to your request is now complete.'
  }
}];
export function RequetesConfigurer() {
  const {
    t,
    lang
  } = useLanguage();
  const [activeSection, setActiveSection] = useState<Section>('departements');
  const [departements] = useState<Departement[]>(MOCK_DEPARTEMENTS);
  const [categories] = useState<Categorie[]>(MOCK_CATEGORIES);
  const [statuts] = useState<Statut[]>(MOCK_STATUTS);
  const [messages, setMessages] = useState<MessageTpl[]>(MOCK_MESSAGES);
  const updateMessage = (idx: number, field: 'titre' | 'contenu', value: string) => {
    setMessages(prev => prev.map((m, i) => i === idx ? {
      ...m,
      [field]: {
        ...m[field],
        [lang]: value
      }
    } : m));
  };
  const thClass = 'px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide';
  const addBtnClass = 'px-4 py-2 text-sm font-medium text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors flex items-center gap-2';
  const rowActions = <div className="flex items-center justify-center gap-2" data-id="element-5685" data-mp="28w92boukaufp">
      <button className="text-slate-400 hover:text-[#4F46E5]" aria-label={t('Modifier', 'Edit')} data-id="element-5686" data-mp="2dnqupoiirmp2">
        <PencilIcon className="w-4 h-4" data-id="element-5687" data-mp="2indiuszvyncn" />
      </button>
      <button className="text-slate-400 hover:text-red-600" aria-label={t('Supprimer', 'Delete')} data-id="element-5688" data-mp="2imlqdr1l55ql">
        <TrashIcon className="w-4 h-4" data-id="element-5689" data-mp="2nkddm7rac3og" />
      </button>
    </div>;
  const renderToolbar = () => <div className="flex items-center gap-1 p-2 bg-slate-50 border border-slate-200 rounded-t-lg flex-wrap" data-id="element-5690" data-mp="2robd4jkivgi1">
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white" data-id="element-5691" data-mp="2wm3dm1z8gaxu">
        <option data-id="element-5692" data-mp="xbbsgc1abw3w">Sans Serif</option>
      </select>
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white ml-2" data-id="element-5693" data-mp="128lnc77g0zz4">
        <option data-id="element-5694" data-mp="6yoz40tw3v7o">{t('Normal', 'Normal')}</option>
      </select>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-5695" data-mp="6wuaocjetkyu">
        <button className="p-1 hover:bg-slate-200 rounded" title="H1" data-id="element-5696" data-mp="1ysuq0ha61rl"><span className="text-xs font-bold" data-id="element-5697" data-mp="gxeg7rdypbmv">H₁</span></button>
        <button className="p-1 hover:bg-slate-200 rounded" title="H2" data-id="element-5698" data-mp="gus89qn5su44"><span className="text-xs font-bold" data-id="element-5699" data-mp="bytgru6javzh">H₂</span></button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-5700" data-mp="3kdbply4aufk5">
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Gras', 'Bold')} data-id="element-5701" data-mp="3feif8ekb74o7"><BoldIcon className="w-4 h-4" data-id="element-5702" data-mp="3fce1z60istb9" /></button>
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Italique', 'Italic')} data-id="element-5703" data-mp="3u3mubmvt4ss3"><ItalicIcon className="w-4 h-4" data-id="element-5704" data-mp="3pc4tqzxp4u7n" /></button>
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Souligné', 'Underline')} data-id="element-5705" data-mp="3pbdb0pp76qys"><UnderlineIcon className="w-4 h-4" data-id="element-5706" data-mp="31livu1heoqbd" /></button>
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Barré', 'Strikethrough')} data-id="element-5707" data-mp="2wois3x813znf"><StrikethroughIcon className="w-4 h-4" data-id="element-5708" data-mp="1c68862epm5ko" /></button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-5709" data-mp="1c3vkd8m9t0zp">
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Lien', 'Link')} data-id="element-5710" data-mp="1gxzqxqrmj709"><LinkIcon className="w-4 h-4" data-id="element-5711" data-mp="1293k1x1b0018" /></button>
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Code', 'Code')} data-id="element-5712" data-mp="176vccs5v1o5y"><CodeIcon className="w-4 h-4" data-id="element-5713" data-mp="174spgh008b5g" /></button>
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Citation', 'Quote')} data-id="element-5714" data-mp="1uw6i6o22i8qy"><QuoteIcon className="w-4 h-4" data-id="element-5715" data-mp="1zw2mkoeog271" /></button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-5716" data-mp="1ztgdgkc51emp">
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Couleur du texte', 'Text color')} data-id="element-5717" data-mp="1lvi27nt45ybf"><span className="text-xs font-bold" data-id="element-5718" data-mp="1pvjtd31839cg">A</span></button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-5719" data-mp="afjmx27ceg1r">
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Liste à puces', 'Bulleted list')} data-id="element-5720" data-mp="eipt69ugg788"><ListIcon className="w-4 h-4" data-id="element-5721" data-mp="2h5eokgqe7ctw" /></button>
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Liste numérotée', 'Numbered list')} data-id="element-5722" data-mp="2h43lyk8oob4d"><ListOrderedIcon className="w-4 h-4" data-id="element-5723" data-mp="2c7v95dhyku0d" /></button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-5724" data-mp="27e0ifq3xrers">
        <button className="p-1 hover:bg-slate-200 rounded" aria-label={t('Aligner à gauche', 'Align left')} data-id="element-5725" data-mp="22gqs888q2z2w"><AlignLeftIcon className="w-4 h-4" data-id="element-5726" data-mp="1yenflgabhog1" /></button>
      </div>
    </div>;
  const renderDepartements = () => <div className="space-y-4" data-id="element-5727" data-mp="1te96duwzc8gf">
      <div className="flex items-center justify-between" data-id="element-5728" data-mp="1tdzfm5441x9o">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5729" data-mp="1ofegk90lhjmt">{t('Départements', 'Departments')}</h3>
        <button className={addBtnClass} data-id="element-5730" data-mp="1jhwg770n8cqf">
          <PlusIcon className="w-4 h-4" data-id="element-5731" data-mp="1jixpb5y3mp5i" /> {t('Ajouter un département', 'Add a department')}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-5732" data-mp="3irusnqg2nrzl">
        <table className="w-full" data-id="element-5733" data-mp="3dt1dltl4zsxb">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5734" data-mp="3mco80vu2xi1q">
            <tr data-id="element-5735" data-mp="3mabssqfphlsd">
              <th className={thClass} data-id="element-5736" data-mp="3qdq639c9fo24">{t('Nom', 'Name')}</th>
              <th className={thClass} data-id="element-5737" data-mp="3qbx1dzl7jdwr">{t('Responsable', 'Manager')}</th>
              <th className={`${thClass} text-center w-24`} data-id="element-5738" data-mp="3v5hrl5gk73le">{t('Actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5739" data-mp="3xnbfm1mpxtv">
            {departements.map(dept => <tr key={dept.id} className="hover:bg-slate-50" data-id="element-5740" data-mp="33elogwkawdbb">
                <td className="px-4 py-3 text-sm text-slate-700 font-medium" data-id="element-5741" data-mp="37em0kajxgcf7">{dept.nom[lang]}</td>
                <td className="px-4 py-3 text-sm text-slate-600" data-id="element-5742" data-mp="3ce8eynbhrypl">{dept.responsable}</td>
                <td className="px-4 py-3 text-center" data-id="element-5743" data-mp="3hd1vyppzm7ui">{rowActions}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  const renderCategories = () => <div className="space-y-4" data-id="element-5744" data-mp="rnhn40a7pgcj">
      <div className="flex items-center justify-between" data-id="element-5745" data-mp="wmkjkwc4me8w">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5746" data-mp="wkzq7a1jt1cm">{t('Catégories', 'Categories')}</h3>
        <button className={addBtnClass} data-id="element-5747" data-mp="1hrejskde5f83">
          <PlusIcon className="w-4 h-4" data-id="element-5748" data-mp="1dp9s3r6i6so3" /> {t('Ajouter une catégorie', 'Add a category')}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-5749" data-mp="1rh7znxkkggi5">
        <table className="w-full" data-id="element-5750" data-mp="1mq81yq3mhuel">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5751" data-mp="1mowv2hevs6j9">
            <tr data-id="element-5752" data-mp="21em4t111hiv5">
              <th className={thClass} data-id="element-5753" data-mp="21e2rjxmc3ing">{t('Nom', 'Name')}</th>
              <th className={thClass} data-id="element-5754" data-mp="1wfrktvrc5o9g">{t('Département', 'Department')}</th>
              <th className={thClass} data-id="element-5755" data-mp="1wfhvmae47l4t">{t('SLA (jours)', 'SLA (days)')}</th>
              <th className={`${thClass} text-center w-24`} data-id="element-5756" data-mp="2ae951ji18imm">{t('Actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5757" data-mp="25h8vgylz4xqr">
            {categories.map(cat => {
            const dept = departements.find(d => d.id === cat.departementId);
            return <tr key={cat.id} className="hover:bg-slate-50" data-id="element-5758" data-mp="b3q3e4apq7cu">
                  <td className="px-4 py-3 text-sm text-slate-700 font-medium" data-id="element-5759" data-mp="2mp9uuufzd6o3">{cat.nom[lang]}</td>
                  <td className="px-4 py-3 text-sm text-slate-600" data-id="element-5760" data-mp="2rpecinsmk7fw">{dept?.nom[lang]}</td>
                  <td className="px-4 py-3 text-sm text-slate-600" data-id="element-5761" data-mp="2dxq1knu43p0i">{cat.sla}</td>
                  <td className="px-4 py-3 text-center" data-id="element-5762" data-mp="2dvnd488ly3xw">{rowActions}</td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
  const renderStatuts = () => <div className="space-y-4" data-id="element-5763" data-mp="23xz9est0xm3l">
      <div className="flex items-center justify-between" data-id="element-5764" data-mp="28y3pihq0seta">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5765" data-mp="28zoiw40llrpk">{t('Statuts', 'Statuses')}</h3>
        <button className={addBtnClass} data-id="element-5766" data-mp="1v06q7dk6luax">
          <PlusIcon className="w-4 h-4" data-id="element-5767" data-mp="1z07h3xogvxyv" /> {t('Ajouter un statut', 'Add a status')}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-5768" data-mp="3pcwl9ymad9jw">
        <table className="w-full" data-id="element-5769" data-mp="3te8cu9oq04uq">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5770" data-mp="3tei1nty145yu">
            <tr data-id="element-5771" data-mp="23qqr1p39zogm">
              <th className={thClass} data-id="element-5772" data-mp="23q8okaq0se09">{t('Nom', 'Name')}</th>
              <th className={thClass} data-id="element-5773" data-mp="23onxiv2wzd75">{t('Couleur', 'Color')}</th>
              <th className={`${thClass} text-center w-24`} data-id="element-5774" data-mp="28i8uqkwoo35a">{t('Actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5775" data-mp="1tqhta6tmxuz4">
            {statuts.map(statut => <tr key={statut.id} className="hover:bg-slate-50" data-id="element-5776" data-mp="1ys769s0f4ipu">
                <td className="px-4 py-3 text-sm text-slate-700 font-medium" data-id="element-5777" data-mp="2mgqbtzs32he8">{statut.nom[lang]}</td>
                <td className="px-4 py-3 text-sm text-slate-600" data-id="element-5778" data-mp="2rgutvu8n3k6k">
                  <div className="flex items-center gap-2" data-id="element-5779" data-mp="2rdyyodo6f9lt">
                    <div className={`w-4 h-4 rounded-full ${statut.couleur}`} data-id="element-5780" data-mp="2dfsbbrguss0e"></div>
                    <span className="text-xs text-slate-500" data-id="element-5781" data-mp="2hh43a3n79pbr">{statut.couleur}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center" data-id="element-5782" data-mp="1203kgp0hb8ub">{rowActions}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  const renderMessages = () => <div className="space-y-6" data-id="element-5783" data-mp="yifwj5ikowr2">
      <div className="flex items-center justify-between" data-id="element-5784" data-mp="yhmo803ytz6t">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5785" data-mp="tl5wyx1gz4pi">{t('Messages automatisés', 'Automated messages')}</h3>
        <button className={addBtnClass} data-id="element-5786" data-mp="18gltd8pivcp2">
          <PlusIcon className="w-4 h-4" data-id="element-5787" data-mp="18fau9m70ut4a" /> {t('Nouveau message', 'New message')}
        </button>
      </div>

      <div className="space-y-6" data-id="element-5788" data-mp="fszhld6551r4">
        {messages.map((msg, idx) => <div key={msg.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5789" data-mp="fthl8uv4ui92" data-mpi={idx}>
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between" data-id="element-5790" data-mp="avg1s8tsogx2">
              <input type="text" value={msg.titre[lang]} onChange={e => updateMessage(idx, 'titre', e.target.value)} aria-label={t('Titre du message', 'Message title')} className="font-semibold text-slate-700 bg-transparent border-none focus:outline-none focus:ring-0 w-full" data-id="element-5791" data-mp="pkvsjotbsmcr" />
              <button className="text-slate-400 hover:text-red-600 ml-4" aria-label={t('Supprimer', 'Delete')} data-id="element-5792" data-mp="3o89snas0dhb1">
                <TrashIcon className="w-4 h-4" data-id="element-5793" data-mp="hran00vch7oc" />
              </button>
            </div>
            <div className="p-4" data-id="element-5794" data-mp="lrl4oasi1mj1">
              {renderToolbar()}
              <textarea value={msg.contenu[lang]} onChange={e => updateMessage(idx, 'contenu', e.target.value)} rows={4} aria-label={t('Contenu du message', 'Message content')} className="w-full px-3 py-2 border border-slate-200 border-t-0 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5795" data-mp="qjkwffyf194j" />
            </div>
          </div>)}
      </div>
    </div>;
  const sections: {
    id: Section;
    label: string;
  }[] = [{
    id: 'departements',
    label: t('Départements', 'Departments')
  }, {
    id: 'categories',
    label: t('Catégories', 'Categories')
  }, {
    id: 'statuts',
    label: t('Statuts', 'Statuses')
  }, {
    id: 'messages',
    label: t('Messages', 'Messages')
  }];
  return <div className="flex gap-6" data-id="element-5796" data-mp="1o6jyk67vn99t">
      {/* Section Switcher */}
      <div className="w-48 flex-shrink-0" data-id="element-5797" data-mp="38pnnoxkibksl">
        <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 sticky top-4" data-id="element-5798" data-mp="38occ4bktl8qx">
          {sections.map(s => <button key={s.id} onClick={() => setActiveSection(s.id)} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5799" data-mp="33rcgkulljoe2">
              {s.label}
            </button>)}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-5800" data-mp="2kz1gt9m8o6k6">
        {activeSection === 'departements' && renderDepartements()}
        {activeSection === 'categories' && renderCategories()}
        {activeSection === 'statuts' && renderStatuts()}
        {activeSection === 'messages' && renderMessages()}
      </div>
    </div>;
}